import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
  rectIntersection, closestCenter,
} from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import CircularProgress from '@mui/material/CircularProgress';

import KanbanColumn from '@/components/workflow/KanbanColumn';
import AppBadge from '@/components/workflow/AppBadge';
import CommentThread from '@/components/workflow/CommentThread';
import StatusHistoryLog from '@/components/workflow/StatusHistoryLog';
import useAlert from '@/components/feedback/useAlert';
import {
  getBoard, getCards, getCard, createCard, updateCard, moveCard,
  addComment, bulkActivate, getChemists, getGroups, populateGroup,
} from '@/services/workflowService';

const BOARD_ID       = 2;
const ONBOARDING_ID  = 1;
const ALL_APPS = ['Quda','HaloPay','HaloText','DocLink','MediDrop','Zephyr','Webbit'];

const PASTEL_COLORS = [
  { hex: '#fde68a', label: 'Amber'   },
  { hex: '#bbf7d0', label: 'Emerald' },
  { hex: '#bfdbfe', label: 'Blue'    },
  { hex: '#e9d5ff', label: 'Violet'  },
  { hex: '#fecaca', label: 'Rose'    },
  { hex: '#fed7aa', label: 'Orange'  },
  { hex: '#fbcfe8', label: 'Pink'    },
  { hex: '#a5f3fc', label: 'Cyan'    },
  { hex: '#d9f99d', label: 'Lime'    },
  { hex: '#fef08a', label: 'Yellow'  },
];

// ── Reusable confirmation modal ───────────────────────────────────────────────
const ConfirmModal = ({ message, onConfirm, onCancel }) => (
  <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <h3 className="text-sm font-bold text-gray-800">Confirm Action</h3>
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 transition">
          <CloseIcon style={{ fontSize: 18 }} />
        </button>
      </div>
      <p className="px-5 py-4 text-sm text-gray-600 leading-relaxed">{message}</p>
      <div className="flex gap-2 px-5 pb-4">
        <button onClick={onCancel}
          className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition font-medium">
          No
        </button>
        <button onClick={onConfirm}
          className="flex-1 px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-semibold hover:bg-violet-700 transition">
          Yes
        </button>
      </div>
    </div>
  </div>
);

const READY_KEY = 'ready_to_roll_out';

const GroupDealCard = ({ deal, onClick }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: String(deal.id) });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };

  return (
    <div
      ref={setNodeRef} style={style} {...attributes} {...listeners}
      onClick={() => onClick(deal)}
      className="bg-white rounded-lg shadow-sm border border-gray-100 p-3 cursor-pointer hover:shadow-md hover:border-violet-200 transition-all duration-150 group"
    >
      <div className="flex items-center gap-2">
        {deal.color && (
          <span
            className="w-2.5 h-2.5 rounded-full flex-shrink-0 ring-1 ring-black/10"
            style={{ backgroundColor: deal.color }}
          />
        )}
        <p className="text-sm font-semibold text-gray-800 leading-tight group-hover:text-violet-700 transition-colors">
          {deal.group_name}
        </p>
      </div>
      {deal.group_contact_name && (
        <p className="text-xs text-gray-500 mt-0.5 ml-4">{deal.group_contact_name}</p>
      )}
    </div>
  );
};

const NewGroupDealModal = ({ onClose, onCreated, firstColumnId }) => {
  const [form, setForm] = useState({ group_name: '', group_contact_name: '', group_contact_phone: '', group_contact_email: '', group_brand_id: '' });
  const [groups, setGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    getGroups().then(g => setGroups(g || [])).catch(console.error);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        board_id: BOARD_ID,
        column_id: firstColumnId,
        ...form,
        group_brand_id: form.group_brand_id ? Number(form.group_brand_id) : null,
      };
      await createCard(payload);
      onCreated();
      onClose();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-[#002169]">New Group Deal</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition"><CloseIcon /></button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-3">
          {[
            { key: 'group_name', label: 'Group Name *', required: true },
            { key: 'group_contact_name', label: 'Contact Name' },
            { key: 'group_contact_phone', label: 'Contact Phone' },
            { key: 'group_contact_email', label: 'Contact Email', type: 'email' },
          ].map(({ key, label, required, type }) => (
            <div key={key}>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</label>
              <input
                required={required} type={type || 'text'} value={form[key]}
                onChange={(e) => set(key, e.target.value)}
                className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
              />
            </div>
          ))}

          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Link to Group Brand <span className="text-gray-400 font-normal normal-case">(optional — enables bulk populate)</span>
            </label>
            <select
              value={form.group_brand_id}
              onChange={(e) => set('group_brand_id', e.target.value)}
              className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
            >
              <option value="">No group brand linked</option>
              {groups.map(g => (
                <option key={g.group_brand_id} value={g.group_brand_id}>{g.group_brand_name}</option>
              ))}
            </select>
          </div>

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 transition">
              {saving ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const BulkActivatePanel = ({ groupCardId, pharmacies, onboardingColumns, onClose, onActivated }) => {
  const [selections, setSelections] = useState(
    pharmacies.map(p => ({ id: p.id, column_id: onboardingColumns[0]?.id || 1, selected: true }))
  );
  const [activating, setActivating] = useState(false);

  const toggle = (id, field, val) =>
    setSelections(prev => prev.map(s => s.id === id ? { ...s, [field]: val } : s));

  const handleActivate = async () => {
    setActivating(true);
    try {
      const items = selections
        .filter(s => s.selected)
        .map(s => ({ card_id: s.id, to_column_id: s.column_id }));
      await bulkActivate(groupCardId, items);
      onActivated();
      onClose();
    } catch (e) { console.error(e); }
    setActivating(false);
  };

  const selectedCount = selections.filter(s => s.selected).length;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 bg-sky-500 text-white rounded-t-2xl">
          <div className="flex items-center gap-2">
            <RocketLaunchOutlinedIcon />
            <h2 className="font-bold text-lg">Bulk Activate Pharmacies</h2>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white"><CloseIcon /></button>
        </div>
        <div className="px-6 py-4 max-h-96 overflow-y-auto">
          {pharmacies.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No pharmacies to activate.</p>
          ) : (
            <div className="space-y-2">
              {pharmacies.map(p => {
                const sel = selections.find(s => s.id === p.id);
                return (
                  <div key={p.id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-100">
                    <input type="checkbox" checked={sel?.selected || false}
                      onChange={(e) => toggle(p.id, 'selected', e.target.checked)}
                      className="w-4 h-4 rounded accent-sky-500"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800">
                        {p.pharmacy_name || p.chemist_name || `Pharmacy #${p.id}`}
                      </p>
                      {p.primary_app_focus && <AppBadge app={p.primary_app_focus} size="xs" />}
                    </div>
                    <select
                      value={sel?.column_id || onboardingColumns[0]?.id || 1}
                      onChange={(e) => toggle(p.id, 'column_id', Number(e.target.value))}
                      disabled={!sel?.selected}
                      className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none disabled:opacity-40"
                    >
                      {onboardingColumns.map(col => (
                        <option key={col.id} value={col.id}>{col.label}</option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex gap-2 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition">Cancel</button>
          <button onClick={handleActivate} disabled={activating || selectedCount === 0}
            className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 bg-sky-500 text-white rounded-lg text-sm font-semibold hover:bg-sky-600 disabled:opacity-50 transition">
            <RocketLaunchOutlinedIcon style={{ fontSize: 16 }} />
            {activating ? 'Activating…' : `Activate ${selectedCount}`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Searchable chemist combobox ───────────────────────────────────────────────
const ChemistCombobox = ({ chemists, value, onChange, disabled }) => {
  const [query, setQuery]   = useState('');
  const [open, setOpen]     = useState(false);
  const wrapRef             = useRef(null);
  const inputRef            = useRef(null);

  // Derive display name from current value
  const selected = chemists.find(c => String(c.chemistID) === String(value));

  const filtered = useMemo(() => {
    if (!query.trim()) return chemists;
    const q = query.toLowerCase();
    return chemists.filter(c => (c.chemistName || '').toLowerCase().includes(q));
  }, [query, chemists]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (chemist) => {
    onChange(String(chemist.chemistID));
    setQuery('');
    setOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setQuery('');
    setOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div ref={wrapRef} className="relative">
      <div
        className={`flex items-center gap-1 mt-1 border rounded-lg bg-white overflow-hidden focus-within:ring-2 focus-within:ring-violet-300 transition
          ${disabled ? 'opacity-50 pointer-events-none' : ''} border-gray-200`}
      >
        <input
          ref={inputRef}
          type="text"
          value={open ? query : (selected?.chemistName || '')}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); if (!e.target.value) onChange(''); }}
          onFocus={() => { setOpen(true); setQuery(''); }}
          placeholder="Search chemist…"
          className="flex-1 px-3 py-2 text-sm focus:outline-none bg-transparent"
        />
        {value && (
          <button type="button" onClick={handleClear} className="px-2 text-gray-400 hover:text-gray-600 transition">
            <CloseIcon style={{ fontSize: 14 }} />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl max-h-52 overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="px-4 py-3 text-xs text-gray-400 italic">No chemists match "{query}"</p>
          ) : (
            filtered.map(c => (
              <button
                key={c.chemistID}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); handleSelect(c); }}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-violet-50 hover:text-violet-700 transition
                  ${String(c.chemistID) === String(value) ? 'bg-violet-50 text-violet-700 font-semibold' : 'text-gray-700'}`}
              >
                {c.chemistName}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const GroupDealDetailDrawer = ({ dealId, boardColumns, onboardingColumns, onClose, onUpdate }) => {
  const { showAlert, AlertSnackbar } = useAlert();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chemists, setChemists] = useState([]);
  const [activeTab, setActiveTab] = useState('details');
  const [showActivate, setShowActivate] = useState(false);
  const [attachForm, setAttachForm] = useState({ chemist_id: '', primary_app_focus: 'Quda' });
  const [attachSaving, setAttachSaving] = useState(false);
  const [fieldError, setFieldError] = useState('');
  const [saveMsg, setSaveMsg] = useState('');
  const [populating, setPopulating] = useState(false);
  const [populateResult, setPopulateResult] = useState(null);
  const [showPopulateConfirm, setShowPopulateConfirm] = useState(false);
  const saveMsgTimer = useRef(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const deal = await getCard(dealId);
      setData(deal);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [dealId]);

  useEffect(() => { reload(); }, [reload]);

  // Chemists list (exclude already-linked) — load once on mount only
  useEffect(() => {
    getChemists(true).then(c => setChemists(c || [])).catch(console.error);
  }, []);

  const handleColumnChange = async (columnId) => {
    try {
      await moveCard(dealId, Number(columnId));
      setData(d => ({ ...d, column_id: Number(columnId) }));
      onUpdate && onUpdate();
    } catch (e) { console.error(e); }
  };

  const handleFieldBlur = async (key, value) => {
    if (key === 'group_name' && !value.trim()) {
      setFieldError('Group Name is required.');
      return;
    }
    setFieldError('');
    try {
      await updateCard(dealId, { [key]: value });
      clearTimeout(saveMsgTimer.current);
      setSaveMsg('Changes saved');
      saveMsgTimer.current = setTimeout(() => setSaveMsg(''), 2500);
      // Refresh board so the card title updates with the new name
      if (key === 'group_name') onUpdate && onUpdate();
    } catch (e) { console.error(e); }
  };

  const handlePopulateGroup = () => setShowPopulateConfirm(true);

  const handlePopulateConfirmed = async () => {
    setShowPopulateConfirm(false);
    setPopulating(true);
    setPopulateResult(null);
    try {
      const result = await populateGroup(dealId);
      setPopulateResult(result);
      // Reload whenever anything changed (created OR linked)
      if (result.created > 0 || result.linked > 0) {
        onUpdate && onUpdate();
        await reload();
      }
      // Switch to pharmacies tab so the refreshed list is immediately visible
      setActiveTab('pharmacies');
    } catch (e) { console.error(e); }
    setPopulating(false);
  };

  const handleAttach = async (e) => {
    e.preventDefault();
    if (!attachForm.chemist_id) return;
    setAttachSaving(true);
    try {
      // Find full chemist record so we can pre-populate pharmacy fields on the new card
      const chemist = chemists.find(c => String(c.chemistID) === String(attachForm.chemist_id));
      await createCard({
        board_id:          ONBOARDING_ID,
        column_id:         onboardingColumns[0]?.id || 1,
        chemist_id:        Number(attachForm.chemist_id),
        pharmacy_name:     chemist?.chemistName  || '',
        pharmacy_phone:    chemist?.phone        || '',
        pharmacy_email:    chemist?.email        || '',
        primary_app_focus: attachForm.primary_app_focus,
        parent_card_id:    dealId,
      });
      setAttachForm({ chemist_id: '', primary_app_focus: 'Quda' });
      await reload();
      showAlert({ severity: 'success', message: 'Pharmacy attached to pipeline.' });
    } catch (err) {
      if (err.status === 409) {
        showAlert({
          severity: 'warning',
          title:    'Already in Pipeline',
          message:  'This chemist already has an active lead in the onboarding pipeline.',
        });
      } else {
        console.error(err);
        showAlert({ severity: 'error', message: err.message || 'Failed to attach pharmacy.' });
      }
    }
    setAttachSaving(false);
  };

  const handleRemovePharmacy = async (childCardId) => {
    if (!window.confirm('Remove this pharmacy from the group?')) return;
    try { await updateCard(childCardId, {}); /* soft detach by removing parent? */ await reload(); } catch (e) { console.error(e); }
  };

  const handleAddComment = async (comment) => {
    await addComment(dealId, comment);
    await reload();
  };

  const TABS = ['details','pharmacies','history'];
  const isReadyToRollOut = boardColumns.find(c => c.id === data?.column_id)?.key === READY_KEY;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30 backdrop-blur-sm">
      <div className="w-full max-w-xl bg-white shadow-2xl flex flex-col h-full">
        <div className="flex items-center justify-between px-6 py-4 bg-violet-700 text-white flex-shrink-0">
          <div className="flex items-center gap-3">
            <GroupsOutlinedIcon />
            <h2 className="text-lg font-bold">{data?.group_name || 'Loading…'}</h2>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white transition"><CloseIcon /></button>
        </div>

        <div className="flex border-b border-gray-100 flex-shrink-0 bg-gray-50">
          {TABS.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-4 py-2.5 text-xs font-semibold uppercase tracking-wide transition-all
                ${activeTab === tab ? 'border-b-2 border-violet-500 text-violet-600 bg-white' : 'text-gray-500 hover:text-gray-700'}`}>
              {tab}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center py-12"><CircularProgress size={28} style={{ color: '#7c3aed' }} /></div>
          ) : !data ? <p className="text-gray-400 text-sm">Not found.</p> : (
            <>
              {activeTab === 'details' && (
                <div className="space-y-3">
                  {/* Status */}
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</label>
                    <select value={data.column_id} onChange={(e) => handleColumnChange(e.target.value)}
                      className="mt-0.5 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300">
                      {boardColumns.map(col => <option key={col.id} value={col.id}>{col.label}</option>)}
                    </select>
                  </div>

                  {saveMsg && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700 font-semibold">
                      <span>✓</span> {saveMsg}
                    </div>
                  )}

                  {/* Colour tag */}
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Group Colour <span className="text-gray-400 font-normal normal-case">(shown on linked onboarding cards)</span>
                    </label>
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      {PASTEL_COLORS.map(({ hex, label }) => (
                        <button
                          key={hex}
                          type="button"
                          title={label}
                          onClick={async () => {
                            const newColor = data.color === hex ? null : hex;
                            setData(d => ({ ...d, color: newColor }));
                            await updateCard(dealId, { color: newColor });
                            onUpdate && onUpdate();
                          }}
                          className="w-6 h-6 rounded-full ring-1 ring-black/10 transition-transform hover:scale-110 focus:outline-none"
                          style={{
                            backgroundColor: hex,
                            boxShadow: data.color === hex ? `0 0 0 2px white, 0 0 0 3.5px ${hex}` : undefined,
                          }}
                        />
                      ))}
                      {data.color && (
                        <button
                          type="button"
                          title="Clear colour"
                          onClick={async () => {
                            setData(d => ({ ...d, color: null }));
                            await updateCard(dealId, { color: null });
                            onUpdate && onUpdate();
                          }}
                          className="w-6 h-6 rounded-full bg-gray-100 ring-1 ring-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition text-[10px] font-bold"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>

                  {/* 2-col fields */}
                  <div className="grid grid-cols-2 gap-2">
                    {/* Group Name – full width */}
                    <div className="col-span-2">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Group Name<span className="text-red-500 ml-0.5">*</span>
                      </label>
                      <input
                        value={data.group_name || ''}
                        onChange={(e) => { setData(d => ({ ...d, group_name: e.target.value })); if (fieldError) setFieldError(''); }}
                        onBlur={(e) => handleFieldBlur('group_name', e.target.value)}
                        className={`mt-0.5 w-full border rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 ${fieldError ? 'border-red-400' : 'border-gray-200'}`}
                      />
                      {fieldError && <p className="mt-0.5 text-xs text-red-500">{fieldError}</p>}
                    </div>

                    {/* Contact Name | Phone */}
                    {[
                      { key: 'group_contact_name', label: 'Contact Name' },
                      { key: 'group_contact_phone', label: 'Phone' },
                    ].map(({ key, label }) => (
                      <div key={key}>
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</label>
                        <input
                          value={data[key] || ''}
                          onChange={(e) => setData(d => ({ ...d, [key]: e.target.value }))}
                          onBlur={(e) => handleFieldBlur(key, e.target.value)}
                          className="mt-0.5 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
                        />
                      </div>
                    ))}

                    {/* Email – full width */}
                    <div className="col-span-2">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Contact Email</label>
                      <input
                        value={data.group_contact_email || ''}
                        onChange={(e) => setData(d => ({ ...d, group_contact_email: e.target.value }))}
                        onBlur={(e) => handleFieldBlur('group_contact_email', e.target.value)}
                        className="mt-0.5 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
                      />
                    </div>
                  </div>

                  {/* Group brand link */}
                  {data.group_brand_id && (
                    <div className="p-3 rounded-xl border border-violet-100 bg-violet-50 space-y-3">
                      <div className="flex items-center gap-2">
                        <GroupsOutlinedIcon style={{ fontSize: 16, color: '#7c3aed' }} />
                        <div>
                          <p className="text-xs font-semibold text-violet-700 uppercase tracking-wide">Linked Group Brand</p>
                          <p className="text-sm font-bold text-violet-900">{data.group_brand_name}</p>
                        </div>
                      </div>

                      {populateResult && (
                        <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-700 space-y-0.5">
                          <p className="font-bold">✓ Done</p>
                          {populateResult.created > 0 && (
                            <p>{populateResult.created} new lead{populateResult.created !== 1 ? 's' : ''} created</p>
                          )}
                          {populateResult.linked > 0 && (
                            <p>{populateResult.linked} existing lead{populateResult.linked !== 1 ? 's' : ''} linked to this deal</p>
                          )}
                          {populateResult.skipped > 0 && (
                            <p className="text-emerald-500">{populateResult.skipped} already linked — skipped</p>
                          )}
                        </div>
                      )}

                      <button
                        onClick={handlePopulateGroup}
                        disabled={populating}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-violet-600 text-white text-sm font-bold rounded-lg hover:bg-violet-700 disabled:opacity-50 transition"
                      >
                        <RocketLaunchOutlinedIcon style={{ fontSize: 16 }} />
                        {populating ? 'Populating…' : 'Populate from Group'}
                      </button>
                      <p className="text-[10px] text-violet-500 text-center">
                        Creates an onboarding lead card for each active chemist in this group not yet in the pipeline.
                      </p>
                    </div>
                  )}

                  {isReadyToRollOut && (
                    <button onClick={() => setShowActivate(true)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-sky-500 text-white font-bold rounded-xl hover:bg-sky-600 transition shadow">
                      <RocketLaunchOutlinedIcon /> Bulk Activate Pharmacies
                    </button>
                  )}

                  {/* Inline comments */}
                  <div className="border-t border-gray-100 pt-3">
                    <CommentThread comments={data.comments || []} onAdd={handleAddComment} />
                  </div>
                </div>
              )}

              {activeTab === 'pharmacies' && (
                <div className="space-y-4">

                  {/* Attach individual chemist */}
                  <form onSubmit={handleAttach} className="p-4 rounded-xl border border-violet-100 bg-violet-50 space-y-3">
                    <p className="text-xs font-bold text-violet-700 uppercase tracking-wide">Attach Chemist Individually</p>
                    <div>
                      <label className="text-xs text-violet-600 font-medium">Chemist</label>
                      <ChemistCombobox
                        chemists={chemists}
                        value={attachForm.chemist_id}
                        onChange={(id) => setAttachForm(f => ({ ...f, chemist_id: id }))}
                        disabled={attachSaving}
                      />
                      {chemists.length === 0 && (
                        <p className="mt-1 text-[10px] text-violet-400">All active chemists are already in the pipeline.</p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-violet-600 font-medium">Primary App Focus</label>
                      <select value={attachForm.primary_app_focus}
                        onChange={(e) => setAttachForm(f => ({ ...f, primary_app_focus: e.target.value }))}
                        className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-300">
                        {ALL_APPS.map(a => <option key={a} value={a}>{a}</option>)}
                      </select>
                    </div>
                    <button type="submit" disabled={attachSaving || !attachForm.chemist_id}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-violet-600 text-white text-sm font-bold rounded-lg hover:bg-violet-700 disabled:opacity-50 transition">
                      {attachSaving ? 'Attaching…' : 'Attach to Pipeline'}
                    </button>
                  </form>

                  {/* Group brand context banner */}
                  {data.group_brand_name && (
                    <div className="flex items-center gap-3 px-4 py-3 bg-violet-50 border border-violet-100 rounded-xl">
                      <GroupsOutlinedIcon style={{ fontSize: 20, color: '#7c3aed' }} />
                      <div>
                        <p className="text-xs font-bold text-violet-700 uppercase tracking-wide">{data.group_brand_name}</p>
                        <p className="text-xs text-violet-500 mt-0.5">
                          {(data.pharmacies || []).length === 0
                            ? 'No pharmacies in pipeline yet — use "Populate from Group" on the Details tab.'
                            : `${(data.pharmacies || []).length} pharmacie${(data.pharmacies || []).length !== 1 ? 's' : ''} currently in the onboarding pipeline`}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Pharmacy list */}
                  <div className="space-y-2">
                    {[...(data.pharmacies || [])].sort((a, b) => {
                      const nameA = (a.pharmacy_name || a.chemist_name || '').toLowerCase();
                      const nameB = (b.pharmacy_name || b.chemist_name || '').toLowerCase();
                      return nameA.localeCompare(nameB);
                    }).map(p => {
                      const name = p.pharmacy_name || p.chemist_name || `Entry #${p.id}`;
                      const phone = p.pharmacy_phone || p.chemist_phone;
                      const email = p.pharmacy_email || p.chemist_email;
                      return (
                        <div key={p.id} className="flex items-start gap-3 p-3 rounded-xl border border-gray-100 bg-white shadow-sm">
                          <div className="mt-0.5 p-1.5 rounded-lg bg-violet-50">
                            <StorefrontOutlinedIcon style={{ fontSize: 16, color: '#7c3aed' }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800 leading-tight">{name}</p>
                            {phone && <p className="text-xs text-gray-400 mt-0.5">{phone}</p>}
                            {email && <p className="text-xs text-gray-400">{email}</p>}
                            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                              {p.primary_app_focus && <AppBadge app={p.primary_app_focus} size="xs" />}
                              {p.column_label && (
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 uppercase tracking-wide">
                                  {p.column_label}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col gap-1 flex-shrink-0">
                            <button
                              onClick={() => navigate(`/workflow/onboarding?cardId=${p.id}`)}
                              className="p-1.5 rounded-lg text-gray-400 hover:text-violet-600 hover:bg-violet-50 transition"
                            >
                              <OpenInNewOutlinedIcon style={{ fontSize: 16 }} />
                            </button>
                            {p.chemist_uuid && (
                              <button
                                onClick={() => navigate(`/admin-functions/chemist/${p.chemist_uuid}`)}
                                className="p-1.5 rounded-lg text-gray-400 hover:text-sky-600 hover:bg-sky-50 transition"
                              >
                                <PersonOutlinedIcon style={{ fontSize: 16 }} />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {(!data.pharmacies || data.pharmacies.length === 0) && (
                      <div className="flex flex-col items-center py-8 text-center">
                        <StorefrontOutlinedIcon style={{ fontSize: 36, color: '#d1d5db' }} />
                        <p className="text-sm font-medium text-gray-400 mt-3">No pharmacies in pipeline yet</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {data.group_brand_id
                            ? 'Use "Populate from Group" on the Details tab to bulk-add all active group chemists, or attach one below.'
                            : 'Use the form below to attach individual chemists to this deal.'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'history' && (
                <StatusHistoryLog history={data.history || []} />
              )}
            </>
          )}
        </div>
      </div>

      {showActivate && data && (
        <BulkActivatePanel
          groupCardId={dealId}
          pharmacies={data.pharmacies || []}
          onboardingColumns={onboardingColumns}
          onClose={() => setShowActivate(false)}
          onActivated={() => { reload(); onUpdate && onUpdate(); }}
        />
      )}

      {showPopulateConfirm && (
        <ConfirmModal
          message={`This will create an onboarding lead card for every active chemist in the "${data?.group_brand_name}" group who isn't already in the pipeline. Continue?`}
          onConfirm={handlePopulateConfirmed}
          onCancel={() => setShowPopulateConfirm(false)}
        />
      )}

      {AlertSnackbar}
    </div>
  );
};

const GroupDealsBoard = () => {
  const [showNew, setShowNew] = useState(false);
  const [boardColumns, setBoardColumns] = useState([]);
  const [onboardingColumns, setOnboardingColumns] = useState([]);
  const [columns, setColumns] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedDealId, setSelectedDealId] = useState(null);
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const loadBoard = useCallback(async () => {
    setLoading(true);
    try {
      const [board, onboardingBoard, result] = await Promise.all([
        getBoard(BOARD_ID),
        getBoard(ONBOARDING_ID),
        getCards({ board_id: BOARD_ID, limit: 200 }),
      ]);
      const cols = board?.columns || [];
      setBoardColumns(cols);
      setOnboardingColumns(onboardingBoard?.columns || []);

      const cards = result?.cards || [];
      const grouped = {};
      cols.forEach(c => { grouped[String(c.id)] = []; });
      cards.forEach(card => {
        const key = String(card.column_id);
        if (grouped[key]) grouped[key].push(card);
      });
      setColumns(grouped);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadBoard(); }, [loadBoard]);

  const COLUMN_IDS = new Set(boardColumns.map(c => String(c.id)));

  const kanbanCollision = (args) => {
    const colHits = rectIntersection({
      ...args,
      droppableContainers: args.droppableContainers.filter(c => COLUMN_IDS.has(String(c.id))),
    });
    if (colHits.length > 0) return colHits;
    return closestCenter(args);
  };

  const handleDragEnd = async ({ active, over }) => {
    setActiveId(null);
    if (!over) return;

    const activeStr = String(active.id);
    const overStr   = String(over.id);

    let sourceCol = null; let deal = null;
    for (const [col, cards] of Object.entries(columns)) {
      const found = cards.find(c => String(c.id) === activeStr);
      if (found) { sourceCol = col; deal = found; break; }
    }
    if (!sourceCol || !deal) return;

    let targetCol = COLUMN_IDS.has(overStr) ? overStr : null;
    if (!targetCol) {
      for (const [col, cards] of Object.entries(columns)) {
        if (cards.find(c => String(c.id) === overStr)) { targetCol = col; break; }
      }
    }
    if (!targetCol || targetCol === sourceCol) return;

    setColumns(prev => {
      const next = { ...prev };
      next[sourceCol] = next[sourceCol].filter(c => String(c.id) !== activeStr);
      next[targetCol] = [{ ...deal, column_id: Number(targetCol) }, ...next[targetCol]];
      return next;
    });

    try { await moveCard(deal.id, Number(targetCol)); } catch { loadBoard(); }
  };

  const draggedDeal = activeId
    ? Object.values(columns).flat().find(c => String(c.id) === String(activeId))
    : null;

  const firstColumnId = boardColumns[0]?.id || 14;

  if (loading) return <div className="flex justify-center items-center h-full"><CircularProgress style={{ color: '#7c3aed' }} /></div>;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <button
          onClick={() => setShowNew(true)}
          className="flex items-center gap-1.5 px-4 py-2 bg-violet-700 text-white text-sm font-semibold rounded-lg hover:bg-violet-800 transition shadow-sm"
        >
          <AddIcon style={{ fontSize: 18 }} /> New Group Deal
        </button>
      </div>

      <DndContext sensors={sensors} collisionDetection={kanbanCollision}
        onDragStart={({ active }) => setActiveId(active.id)}
        onDragEnd={handleDragEnd}>
        <div
          className="flex gap-3 p-3 overflow-x-auto border border-gray-200 rounded-xl bg-gray-50 min-w-0"
          style={{ height: 'calc(100vh - 170px)' }}
        >
          {boardColumns.map(col => (
            <KanbanColumn key={col.id} id={String(col.id)} label={col.label}
              cards={columns[String(col.id)] || []} accentColor={col.color} headerBg={col.header_color}>
              {(columns[String(col.id)] || []).map(deal => (
                <GroupDealCard key={deal.id} deal={deal} onClick={() => setSelectedDealId(deal.id)} />
              ))}
            </KanbanColumn>
          ))}
        </div>
        <DragOverlay>
          {draggedDeal && (
            <div className="bg-white rounded-lg shadow-lg border border-violet-200 p-3 w-56 rotate-2">
              <p className="text-sm font-semibold text-gray-800">{draggedDeal.group_name}</p>
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {showNew && (
        <NewGroupDealModal
          firstColumnId={firstColumnId}
          onClose={() => setShowNew(false)}
          onCreated={loadBoard}
        />
      )}
      {selectedDealId && (
        <GroupDealDetailDrawer
          dealId={selectedDealId}
          boardColumns={boardColumns}
          onboardingColumns={onboardingColumns}
          onClose={() => setSelectedDealId(null)}
          onUpdate={loadBoard}
        />
      )}
    </div>
  );
};

export default GroupDealsBoard;
